import { delivery } from '~js/utils/delivery';
import { deliveryDrawer } from './delivery-drawer';

const drawerStoreLocater = {
  el: {},
  root: document,

  selectors: {
    buybox: '.js-buybox',
    locateMeContainer: '.js_product_locate_me_container',
    autoCTA: '.js_locateme_auto_cta',
    manualContainer: '.js_locateme_manual_container',
    zipInput: '.js_locateme_manual_input',
    manualCTA: '.js_locate_me_manual_cta',
    deliveringContainer: '.js_deliverying_container',
    deliveryZipText: '.js_delivering_zip',
    manualError: '.js_locateme_manual_error',
    orSpan: '.js_locateme_or',
    detectionFailedErrorBlock: '.js_locateme_error_detection_failed',
    accessDeniedErrorBlock: '.js_locateme_error_access_denied',
    manualSpinner: '.js_locateme_manual_spinner',
    manualCheckText: '.js_locateme_check_text',
    deliverableContainer: '.js_delivery_status_container',
    deliveringInfo: '.js_delivery_info',
  },

  // Utility Functions
  qs(selector, parent = this.root) {
    return parent.querySelector(selector);
  },

  initialiseElement(parentElement) {
    this.root = parentElement;
    for (const [key, selector] of Object.entries(this.selectors)) {
      this.el[key] = this.qs(
        selector,
        key.includes('buybox') ? document : this.root,
      );
    }
  },

  resetDrawerState() {
    this.el.manualCTA && (this.el.manualCTA.disabled = true);

    delivery.hide(this.el.manualError);
    delivery.hide(this.el.locateMeError);

    delivery.show(this.el.manualCheckText);
    delivery.hide(this.el.manualSpinner);

    +delivery.show(this.el.locateMeContainer);

    this.el.locateMeContainer?.classList?.remove('js-product-locate-me');
    this.el.deliveringContainer?.classList?.remove('js-deliverying');

    delivery.show(this.el.autoCTA);
    delivery.show(this.el.orSpan);

    delivery.setText(this.el.deliveryZipText, '');

    if (this.el.zipInput) {
      this.el.zipInput.value = '';
    }
  },

  initialiseEvents() {
    this.el.autoCTA?.addEventListener('click', () => this.handleAutoLocate());
    this.el.manualCTA?.addEventListener('click', () =>
      this.handleManualLocate(),
    );
    this.el.zipInput?.addEventListener('input', () =>
      delivery.handleZipInputChange(this.el.zipInput, this.el.manualCTA),
    );
  },

  async handleAutoLocate() {
    this.el.autoCTA?.classList.add('is-locating');
    delivery.hide(this.el.orSpan);
    delivery.hide(this.el.detectionFailedErrorBlock);
    delivery.hide(this.el.accessDeniedErrorBlock);

    try {
      const { zip, deliverable } = await delivery.handleLocateAutoGeneric();
      if (deliverable === 'not-at-all') {
        delivery.redirectToNearestStoreFinder(zip);
      } else if (deliverable === true) {
        delivery.hide(this.el.locateMeContainer);
        delivery.hide(this.el.ctas);
        this.toggleDeliverability({ zip, deliverable });
        delivery.hide(this.el.availabilityText);
        return true;
      } else if (deliverable === false) {
        delivery.redirectToLegacyBuyBox(zip);
      }
      deliveryDrawer.closeDeliveryOverlay();
    } catch (err) {
      console.error('Location error:', err);
      if (err.code === 1) {
        delivery.show(this.el.accessDeniedErrorBlock); // GeolocationPermissionDenied
      } else {
        delivery.show(this.el.detectionFailedErrorBlock); //Position unavailable
      }
      delivery.hide(this.el.autoCTA);
      delivery.hide(this.el.orSpan);
    } finally {
      this.el.autoCTA?.classList.remove('is-locating');
    }
  },

  async handleManualLocate() {
    const input = this.el.zipInput?.value?.trim();
    const { manualSpinner, manualCheckText, manualError } = this.el;

    if (!input) return;
    const isValidZip = /^\d{5,9}$/.test(input);
    if (!isValidZip) {
      delivery.show(manualError);
    } else {
      delivery.hide(manualError);
      delivery.show(manualSpinner);
      delivery.hide(manualCheckText);

      try {
        const { zip, deliverable } =
          await delivery.handleLocateManualGeneric(input);
        if (deliverable === 'not-at-all') {
          delivery.redirectToNearestStoreFinder(zip);
        } else if (deliverable === true) {
          this.toggleDeliverability({ zip, deliverable });
          return true;
        } else if (deliverable === false) {
          delivery.redirectToLegacyBuyBox(zip);
        }
      } catch (err) {
        console.error(err);
      } finally {
        delivery.hide(manualSpinner);
        delivery.show(manualCheckText);
        if (window.location.pathname.includes('/products/')) {
          deliveryDrawer.closeDeliveryOverlay();
        }
      }
    }
  },

  toggleDeliverability({ zip, deliverable }) {
    const isDeliverable = deliverable;

    this.el.locateMeContainer?.classList?.add('js-product-locate-me');
    this.el.deliveringContainer?.classList?.remove('js-deliverying');

    const zipText = `${zip || ''}`;
    delivery.setText(this.el.deliveryZipText, zipText);
    delivery.toggle(
      this.el.deliveringContainer,
      'js-deliverying',
      isDeliverable,
    );

    const storeLink = this.el.deliveringContainer?.querySelector('a');
    if (storeLink) {
      const baseUrl = storeLink.getAttribute('href').split('?')[0];
      storeLink.setAttribute('href', `${baseUrl}?z=${encodeURIComponent(zip)}`);
    }

    if (isDeliverable && this.el.buybox) {
      const span = this.qs('.js-deliveringzip .zip-text', this.el.buybox);
      const variantContainer = this.qs(
        '.js-product_delivarable',
        this.el.buybox,
      );
      const deliverableContainer = this.qs(
        '.js-product__locateMeContainer',
        this.el.buybox,
      );
      const zipBox = this.qs('.js-deliveringzip', this.el.buybox);
      delivery.toggle(variantContainer, 'is-delivarable', true);
      delivery.toggle(zipBox, 'is-delivarable', true);
      delivery.toggle(deliverableContainer, 'is-delivarable', true);
      delivery.setText(span, `${zipText}`);
    }
    delivery.updateZipInURL(zip);
  },

  init(parentElement) {
    this.initialiseElement(parentElement);
    this.initialiseEvents();
    this.resetDrawerState();
  },
};

export { drawerStoreLocater };
